import { Injectable, Injector } from '@angular/core';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { AppState } from '@store/app.state';
import { Subject } from 'rxjs';
import {
  CustomerStatusDigitalData,
  DigitalData,
  EnvironmentDigitalData,
  GenderDigitalData,
  OfferPriceBlockedReason,
  UserStatusDigitalData,
  YesNoDigitalData,
} from '../../models/digitalData.model';
import { DriversKind, InsuranceSituation, PolicyHolder } from '@core/models/swagger';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { CoverageTypesEnum, Patterns } from '@global/enums';
import { Flow } from '@global/routePaths';
import { OFFERS, PREVIOUS_INSURER_OPTIONS } from '@global/constants';
import SHA256 from 'crypto-js/sha256';
import { findParameterResultById } from '@global/utils';
import { QbItaParameterResult } from '@fwmovilidad/qb-home-ita-components-lib';

dayjs.extend(customParseFormat);

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}
declare let digitalData: DigitalData;
declare let stepView: (stepName: string) => void;
declare let popupView: (popupName: string) => void;

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly destroy$: Subject<boolean> = new Subject<boolean>();
  private readonly store: Store<AppState>;

  constructor(private readonly injector: Injector) {
    this.store = this.injector.get(Store);
    this.initializeDigitalData();
    this.setEnvironment(
      environment.production ? EnvironmentDigitalData.PRODUCTION : EnvironmentDigitalData.DEVELOPMENT
    );
  }

  private initializeDigitalData(): void {
    digitalData = {
      environment: '',
      dl_version: '1.0',
      application: 'web',
      site_version: '1.0',
      page_info: {
        brand: 'verti',
        page_type: 'process',
        country: 'de',
        channel: 'digital business',
        url: '',
        referrer: '',
        language: 'de',
      },
      user: {
        client_user_id: '',
        user_status: '',
        client_product_type: '',
        email: '',
        phone: '',
        first_name: '',
        last_name: '',
        gender: '',
        address: {
          street: '',
          house_number: '',
          city: '',
          zip_code: '',
          country: 'de',
        },
      },
      page_error: {
        error_type: '',
        error_message: '',
      },
      process: {
        process_id: 'qb car',
        full_operation_id: '',
        registration_operation_id: '',
        transaction_id: '',
        flow_id: '',
        step_number: '',
        step_description: '',
        step_common: '',
        step_basic: '',
        step_version: '',
        progress_id: '',
        purchase_price: '',
        modality: '',
        product_name: 'car',
      },
      custom: {
        first_insurance: '',
        existing_car: '',
        insurance_start_date: '',
        hsn: '',
        tsn: '',
        brand: '',
        model: '',
        registration_year: '',
        car_engine_type: '',
        car_variant_type: '',
        car_horsepower: '',
        car_purchase_year: '',
        driven_kilometer: '',
        car_holder: '',
        zip_code: '',
        policyholder_type: '',
        policyholder_birthday: '',
        policyholder_license_date: '',
        car_user: '',
        car_user_birthday: '',
        car_user_licensedate: '',
        additional_car: '',
        customer_id: '',
        previous_insurance_brand: '',
        modality: '',
        insurance_modalities_offered: '',
        insurance_prices_offered: '',
        quoting_id: '',
        car_purpose: '',
        parking_area: '',
        flensburg_points: '',
        sf_class: '',
        claim_report_365_days: '',
        previous_insurance_duration: '',
        profession: '',
        home_ownership: '',
        phone: '',
        payment_method: '',
        payment_freq: '',
        addon_names_offered: '',
        addon_prices_offered: '',
        addon_names_selected: '',
        addon_prices_selected: '',
        customer_status: '',
        prev_insurer_canceled: '',
        offer_price_blocked: '',
        number_of_vehicles: '',
        voucher: '',
      },
    };
  }

  sendStepView(stepName: string): void {
    this.setReferrer(digitalData?.page_info?.url);
    this.setUrl(window.location.href);

    try {
      if (environment.environmentName !== 'PRO') {
        console.log(
          JSON.stringify(
            {
              method: 'stepView',
              stepName,
              digitalData: this.getDigitalData(),
            },
            null,
            2
          )
        );
      }
      stepView(this.encodeValue(stepName));
    } catch (e) {}
  }

  sendPopupView(popupName: string): void {
    try {
      if (environment.environmentName !== 'PRO') {
        console.log(
          JSON.stringify({
            method: 'popupView',
            popupName,
            digitalData: this.getDigitalData(),
          })
        );
      }
      popupView(this.encodeValue(popupName));
    } catch (e) {}
  }

  sendFormError(payload: { form_field: string; error_message: string }): void {
    const event = {
      event: 'form_field_error',
      form_field: payload.form_field,
      error_message: payload.error_message,
    };
    try {
      if (environment.environmentName !== 'PRO') {
        console.log('FormError', event);
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(event);
    } catch {}
  }

  /**
   * Normalizes NFD
   * Removes diacritics except 'ñ' (ES6)
   * Removes invalid characters
   * Removes spaces at the start and the end
   * Transforms into lowercase
   */
  private encodeValue(value?: string): string {
    if (!value && typeof value !== 'string') {
      return '';
    }
    return value
      .normalize('NFD')
      .replace(/([^n\u0300-\u036f]|n(?!\u0303(?![\u0300-\u036f])))[\u0300-\u036f]+/gi, '$1')
      .replace(/[^a-zA-Z 0-9.-|]+/g, '')
      .trim()
      .toLowerCase();
  }

  private humanizeValue(value?: string): string {
    if (!value || typeof value !== 'string') {
      return '';
    }
    return value
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .toLowerCase();
  }

  getDigitalData(): DigitalData {
    return digitalData;
  }

  setEnvironment(environment: EnvironmentDigitalData): void {
    digitalData.environment = this.encodeValue(environment) as EnvironmentDigitalData;
  }

  setDlVersion(dlVersion: string): void {
    digitalData.dl_version = this.encodeValue(dlVersion);
  }

  setApplication(application: string): void {
    digitalData.application = this.encodeValue(application);
  }

  setSiteVersion(siteVersion: string): void {
    digitalData.site_version = this.encodeValue(siteVersion);
  }

  /**
   * Page Info
   */
  setBrandPageInfo(brand: string): void {
    digitalData.page_info.brand = this.encodeValue(brand);
  }

  setPageType(pageType: string): void {
    digitalData.page_info.page_type = this.encodeValue(pageType);
  }

  setCountry(country: string): void {
    digitalData.page_info.country = this.encodeValue(country);
  }

  setChannel(channel: string): void {
    digitalData.page_info.channel = this.encodeValue(channel);
  }

  setUrl(url: string): void {
    digitalData.page_info.url = url;
  }

  setReferrer(referrer: string): void {
    digitalData.page_info.referrer = referrer;
  }

  setLanguage(language: string): void {
    digitalData.page_info.language = this.encodeValue(language);
  }

  /**
   * User
   */
  setClientUserId(clientUserId: string): void {
    digitalData.user.client_user_id = this.encodeValue(clientUserId);
  }

  setUserStatus(userStatus: string): void {
    digitalData.user.user_status = this.encodeValue(userStatus) as UserStatusDigitalData;
  }

  setClientProductType(clientProductType: string): void {
    digitalData.user.client_product_type = this.encodeValue(clientProductType);
  }

  setEmail(policyholder: PolicyHolder): void {
    const email =
      policyholder?.person?.contacts?.email ??
      policyholder?.soleProprietorship?.contacts?.email ??
      policyholder?.company?.contacts?.email;

    if (!email) {
      digitalData.user.email = '';
      return;
    }
    digitalData.user.email = this.encodeValue(email);
  }

  setUserPhone(policyholder: PolicyHolder): void {
    const phone =
      policyholder?.person?.contacts?.phoneNumber ??
      policyholder?.soleProprietorship?.contacts?.phoneNumber ??
      policyholder?.company?.contacts?.phoneNumber;

    const areaCode =
      policyholder?.person?.contacts?.phonePrefix ??
      policyholder?.soleProprietorship?.contacts?.phonePrefix ??
      policyholder?.company?.contacts?.phonePrefix;

    if (!phone) {
      digitalData.user.phone = '';
      return;
    }
    digitalData.user.phone = `+49${areaCode}${phone}`;
  }

  setFirstName(policyholder: PolicyHolder): void {
    const firstName = policyholder?.person?.firstname ?? policyholder?.soleProprietorship?.firstname;

    if (!firstName) {
      digitalData.user.first_name = '';
      return;
    }
    digitalData.user.first_name = firstName.trim().toLowerCase();
  }

  setLastName(policyholder: PolicyHolder): void {
    const lastName = policyholder?.person?.surname ?? policyholder?.soleProprietorship?.surname;

    if (!lastName) {
      digitalData.user.last_name = '';
      return;
    }
    digitalData.user.last_name = lastName.trim().toLowerCase();
  }

  setGender(policyholder: PolicyHolder): void {
    const gender = policyholder?.person?.gender ?? policyholder?.soleProprietorship?.gender;

    if (!gender) {
      digitalData.user.gender = '';
      return;
    }
    digitalData.user.gender = GenderDigitalData[gender.toUpperCase()];
  }

  setStreet(policyholder: PolicyHolder): void {
    const street =
      policyholder?.person?.address?.street ??
      policyholder?.soleProprietorship?.address?.street ??
      policyholder?.company?.address?.street;

    if (!street) {
      digitalData.user.address.street = '';
      return;
    }
    digitalData.user.address.street = this.encodeValue(street);
  }

  setHouseNumber(policyholder: PolicyHolder): void {
    const houseNumber =
      policyholder?.person?.address?.houseNumber ??
      policyholder?.soleProprietorship?.address?.houseNumber ??
      policyholder?.company?.address?.houseNumber;

    if (!houseNumber) {
      digitalData.user.address.house_number = '';
      return;
    }
    digitalData.user.address.house_number = this.encodeValue(houseNumber);
  }

  setCity(policyholder: PolicyHolder): void {
    const city =
      policyholder?.person?.address?.city ??
      policyholder?.soleProprietorship?.address?.city ??
      policyholder?.company?.address?.city;

    if (!city) {
      digitalData.user.address.city = '';
      return;
    }
    digitalData.user.address.city = this.encodeValue(city);
  }

  setUserZipCode(policyholder: PolicyHolder, postcode: string): void {
    const zipCode =
      policyholder?.person?.address?.postcode ??
      policyholder?.soleProprietorship?.address?.postcode ??
      policyholder?.company?.address?.postcode;

    if (!zipCode && postcode) {
      digitalData.user.address.zip_code = this.encodeValue(postcode);
      return;
    }

    if (!zipCode) {
      digitalData.user.address.zip_code = '';
      return;
    }
    digitalData.user.address.zip_code = this.encodeValue(zipCode);
  }

  setUserCountry(country: string): void {
    if (!country) {
      digitalData.user.address.country = '';
      return;
    }
    digitalData.user.address.country = this.encodeValue(country);
  }

  /**
   * Page Error
   */
  setErrorType(errorType: string): void {
    digitalData.page_error.error_type = this.encodeValue(errorType);
  }

  setErrorMessage(errorMessage: string): void {
    digitalData.page_error.error_message = this.encodeValue(errorMessage);
  }

  /**
   * Process
   */
  setProcessId(processId: string): void {
    digitalData.process.process_id = this.encodeValue(processId);
  }

  setFullOperationId(fullOperationId: string): void {
    digitalData.process.full_operation_id = this.encodeValue(fullOperationId);
  }

  setRegistrationOperationId(registrationOperationId: string): void {
    digitalData.process.registration_operation_id = this.encodeValue(registrationOperationId);
  }

  setTransactionId(transactionId: string): void {
    digitalData.process.transaction_id = transactionId;
  }

  setFlowId(flowId: Flow): void {
    const formattedFlowId = flowId === Flow.QUICK_QUOTE ? 'quick-quote' : 'long-quote';

    digitalData.process.flow_id = formattedFlowId;
  }

  setStepNumber(stepNumber: string): void {
    digitalData.process.step_number = this.encodeValue(`step${stepNumber}`);
  }

  setStepDescription(stepDescription: string): void {
    digitalData.process.step_description = this.encodeValue(stepDescription);
  }

  setStepCommon(stepCommon: string): void {
    if (!stepCommon) {
      digitalData.process.step_common = '';
      return;
    }
    digitalData.process.step_common = this.encodeValue(`step${stepCommon}`);
  }

  setStepBasic(stepBasic: string): void {
    if (!stepBasic) {
      digitalData.process.step_basic = '';
      return;
    }
    digitalData.process.step_basic = this.encodeValue(stepBasic);
  }

  setStepVersion(stepVersion: string): void {
    digitalData.process.step_version = this.encodeValue(stepVersion);
  }

  setProgressId(progressId: string): void {
    digitalData.process.progress_id = this.encodeValue(progressId);
  }

  setPurchasePrice(purchasePrice: string): void {
    if (!purchasePrice) {
      digitalData.process.purchase_price = '';
      return;
    }

    digitalData.process.purchase_price = purchasePrice;
  }

  setModalityProcess(modality: string): void {
    digitalData.process.modality = this.encodeValue(modality);
  }

  setProductName(productName: string): void {
    digitalData.process.product_name = this.encodeValue(productName);
  }

  /**
   * Custom
   */
  setFirstInsurance(firstInsurance: InsuranceSituation): void {
    if (!firstInsurance) {
      digitalData.custom.first_insurance = '';
      return;
    }

    const formattedFirstInsurance =
      firstInsurance === InsuranceSituation.FIRST_INSURANCE ? YesNoDigitalData.YES : YesNoDigitalData.NO;

    digitalData.custom.first_insurance = this.encodeValue(formattedFirstInsurance) as YesNoDigitalData;
  }

  setExistingCar(existingCar: string): void {
    if (!existingCar) {
      digitalData.custom.existing_car = '';
      return;
    }

    const formattedExistingCar =
      existingCar === InsuranceSituation.INSURER_CHANGE ? YesNoDigitalData.YES : YesNoDigitalData.NO;

    digitalData.custom.existing_car = this.encodeValue(formattedExistingCar) as YesNoDigitalData;
  }

  setInsuranceStartDate(insuranceStartDate: string): void {
    if (!insuranceStartDate) {
      digitalData.custom.insurance_start_date = '';
      return;
    }

    const formattedInsuranceStartDate = dayjs(insuranceStartDate, Patterns.DATE_DASH_YYYYMMDD).format(
      Patterns.DATE_WITHOUT_SEPARATION_DDMMYYYY
    );

    digitalData.custom.insurance_start_date = this.encodeValue(formattedInsuranceStartDate);
  }

  setHsn(hsn: string): void {
    digitalData.custom.hsn = this.encodeValue(hsn);
  }

  setTsn(tsn: string): void {
    digitalData.custom.tsn = this.encodeValue(tsn);
  }

  setBrand(brand: string): void {
    digitalData.custom.brand = this.encodeValue(brand);
  }

  setModel(model: string): void {
    digitalData.custom.model = this.encodeValue(model);
  }

  setRegistrationYear(registrationYear: string): void {
    digitalData.custom.registration_year = this.encodeValue(registrationYear);
  }

  setCarEngineType(carEngineType: string): void {
    digitalData.custom.car_engine_type = this.encodeValue(carEngineType);
  }

  setCarVariantType(carVariantType: string): void {
    digitalData.custom.car_variant_type = this.encodeValue(carVariantType);
  }

  setCarHorsepower(ps: number, kw: number): void {
    if (!ps || !kw) {
      digitalData.custom.car_horsepower = '';
      return;
    }

    const formattedCarHorsepower = `${ps} ps ${kw} kw`;

    digitalData.custom.car_horsepower = this.encodeValue(formattedCarHorsepower);
  }

  setCarPurchaseYear(carPurchaseYear: string): void {
    digitalData.custom.car_purchase_year = this.encodeValue(carPurchaseYear);
  }

  setDrivenKilometer(drivenKilometer: string): void {
    digitalData.custom.driven_kilometer = this.encodeValue(drivenKilometer);
  }

  setCarHolder(carHolder: string): void {
    if (!carHolder) {
      digitalData.custom.car_holder = '';
      return;
    }
    digitalData.custom.car_holder = this.encodeValue(this.humanizeValue(carHolder));
  }

  setZipCode(zipCode: string): void {
    digitalData.custom.zip_code = this.encodeValue(zipCode);
  }

  setPolicyholderType(policyholderType: string): void {
    digitalData.custom.policyholder_type = this.encodeValue(this.humanizeValue(policyholderType));
  }

  setPolicyholderBirthday(policyholderBirthday: string): void {
    if (!policyholderBirthday || !dayjs(policyholderBirthday).isValid()) {
      digitalData.custom.policyholder_birthday = '';
      return;
    }
    digitalData.custom.policyholder_birthday = this.encodeValue(
      dayjs(policyholderBirthday, Patterns.DATE_DASH_YYYYMMDD).format(Patterns.DATE_WITHOUT_SEPARATION_DDMMYYYY)
    );
  }

  setPolicyholderLicenseDate(policyholderLicenseDate: string): void {
    digitalData.custom.policyholder_license_date = this.encodeValue(policyholderLicenseDate);
  }

  setCarUser(carUser: DriversKind): void {
    if (!carUser) {
      digitalData.custom.car_user = '';
      return;
    }

    const formattedCarUser =
      carUser === DriversKind.POLICYHOLDER || carUser === DriversKind.POLICYHOLDER_AND_PARTNER
        ? YesNoDigitalData.YES
        : YesNoDigitalData.NO;

    digitalData.custom.car_user = this.encodeValue(formattedCarUser) as YesNoDigitalData;
  }

  setCarUserBirthday(carUserBirthdayData: {
    driverKind: DriversKind;
    partnerBirthday: string;
    policyholderBirthday: string;
  }): void {
    if (
      carUserBirthdayData.partnerBirthday &&
      carUserBirthdayData.driverKind === DriversKind.POLICYHOLDER_AND_PARTNER
    ) {
      const formattedCarUserBirthday = dayjs(carUserBirthdayData.partnerBirthday, Patterns.DATE_DASH_YYYYMMDD).format(
        Patterns.DATE_WITHOUT_SEPARATION_DDMMYYYY
      );

      digitalData.custom.car_user_birthday = this.encodeValue(formattedCarUserBirthday);
      return;
    }

    if (carUserBirthdayData.driverKind === DriversKind.POLICYHOLDER) {
      const formattedCarUserBirthday = dayjs(
        carUserBirthdayData.policyholderBirthday,
        Patterns.DATE_DASH_YYYYMMDD
      ).format(Patterns.DATE_WITHOUT_SEPARATION_DDMMYYYY);

      digitalData.custom.car_user_birthday = this.encodeValue(formattedCarUserBirthday);
      return;
    }

    digitalData.custom.car_user_birthday = '';
  }

  setCarUserLicensedate(carUserLicenseDateData: {
    driverKind: DriversKind;
    partnerLicensedate: string;
    policyholderLicensedate: string;
  }): void {
    if (
      carUserLicenseDateData.driverKind === DriversKind.POLICYHOLDER &&
      carUserLicenseDateData?.policyholderLicensedate
    ) {
      digitalData.custom.car_user_licensedate = this.encodeValue(carUserLicenseDateData.policyholderLicensedate);
      return;
    }

    if (
      carUserLicenseDateData.driverKind === DriversKind.POLICYHOLDER_AND_PARTNER &&
      carUserLicenseDateData?.partnerLicensedate
    ) {
      digitalData.custom.car_user_licensedate = this.encodeValue(carUserLicenseDateData.partnerLicensedate);
      return;
    }

    digitalData.custom.car_user_licensedate = '';
  }

  setAdditionalCar(additionalCar: boolean): void {
    if (additionalCar == undefined) {
      digitalData.custom.additional_car = '';
      return;
    }

    const formattedAdditionalCar = additionalCar ? YesNoDigitalData.YES : YesNoDigitalData.NO;

    digitalData.custom.additional_car = this.encodeValue(formattedAdditionalCar) as YesNoDigitalData;
  }

  setCustomerId(customerId: string): void {
    if (!customerId) {
      digitalData.custom.customer_id = '';
      return;
    }

    const emailHash = SHA256(customerId).toString();

    digitalData.custom.customer_id = this.encodeValue(emailHash);
  }

  setPreviousInsuranceBrand(previousInsurer: string, carInsurers: QbItaParameterResult[]): void {
    if (!previousInsurer) {
      digitalData.custom.previous_insurance_brand = '';
      return;
    }

    const previousInsurerFormatted =
      findParameterResultById(carInsurers, previousInsurer)?.parameterName ??
      findParameterResultById(PREVIOUS_INSURER_OPTIONS, previousInsurer)?.parameterName;

    digitalData.custom.previous_insurance_brand = this.encodeValue(previousInsurerFormatted);
  }

  setModality(coverageType: string, productLine: string): void {
    if (!coverageType || !productLine) {
      digitalData.custom.modality = '';
      return;
    }

    const formattedCoverageType = CoverageTypesEnum[coverageType.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase()];

    const formattedProductLine = findParameterResultById(OFFERS, productLine)?.parameterName;

    digitalData.custom.modality = this.encodeValue(`${formattedCoverageType}_${formattedProductLine}`);
  }

  setInsuranceModalitiesOffered(insuranceModalitiesOfferer: string): void {
    digitalData.custom.insurance_modalities_offered = this.encodeValue(insuranceModalitiesOfferer);
  }

  setInsurancePricesOffered(insurancePricesOfferd: string): void {
    digitalData.custom.insurance_prices_offered = this.encodeValue(insurancePricesOfferd);
  }

  setQuotingId(quotingId: string): void {
    digitalData.custom.quoting_id = this.encodeValue(quotingId);
  }

  setCarPurpose(carPurpose: string): void {
    digitalData.custom.car_purpose = this.encodeValue(carPurpose);
  }

  setParkingArea(parkingArea: string): void {
    digitalData.custom.parking_area = this.encodeValue(parkingArea);
  }

  setFlensburgPoints(flensburgPoints: string): void {
    if (!flensburgPoints) {
      digitalData.custom.flensburg_points = '';
      return;
    }

    digitalData.custom.flensburg_points = flensburgPoints === 'OneOrMore' ? YesNoDigitalData.YES : YesNoDigitalData.NO;
  }

  setSfClass(sfClass): void {
    digitalData.custom.sf_class = this.encodeValue(this.humanizeValue(sfClass));
  }

  setClaimReport365Days(claimReport365Days: string): void {
    if (!claimReport365Days) {
      digitalData.custom.claim_report_365_days = '';
      return;
    }

    digitalData.custom.claim_report_365_days = claimReport365Days === 'Ja' ? YesNoDigitalData.YES : YesNoDigitalData.NO;
  }

  setPreviousInsuranceDuration(previousInsuranceDuration: string): void {
    if (!previousInsuranceDuration) {
      digitalData.custom.previous_insurance_duration = '';
      return;
    }
    digitalData.custom.previous_insurance_duration = this.encodeValue(this.humanizeValue(previousInsuranceDuration));
  }

  setProfession(profession: string): void {
    digitalData.custom.profession = this.encodeValue(this.humanizeValue(profession));
  }

  setHomeOwnership(homeOwnership: string): void {
    digitalData.custom.home_ownership = this.encodeValue(homeOwnership);
  }

  setPhone(policyholder: PolicyHolder): void {
    let phone: string;

    if (policyholder?.person?.contacts?.phonePrefix && policyholder?.person?.contacts?.phoneNumber) {
      phone = policyholder?.person?.contacts?.phonePrefix + policyholder?.person?.contacts?.phoneNumber;
    }

    if (
      policyholder?.soleProprietorship?.contacts?.phonePrefix &&
      policyholder?.soleProprietorship?.contacts?.phoneNumber
    ) {
      phone =
        policyholder?.soleProprietorship?.contacts?.phonePrefix +
        policyholder?.soleProprietorship?.contacts?.phoneNumber;
    }

    if (policyholder?.company?.contacts?.phonePrefix && policyholder?.company?.contacts?.phoneNumber) {
      phone = policyholder?.company?.contacts?.phonePrefix + policyholder?.company?.contacts?.phoneNumber;
    }

    if (!phone) {
      digitalData.custom.phone = '';
      return;
    }

    const phoneHash = SHA256(phone).toString();

    digitalData.custom.phone = phoneHash;
  }

  setPaymentMethod(paymentMethod: string): void {
    digitalData.custom.payment_method = this.encodeValue(paymentMethod);
  }

  setPaymentFreq(paymentFreq: string): void {
    digitalData.custom.payment_freq = this.encodeValue(paymentFreq);
  }

  setAddonNamesOffered(addonNamesOffered: string): void {
    digitalData.custom.addon_names_offered = this.encodeValue(addonNamesOffered);
  }

  setAddonPricesOffered(addonPricesOffered: string): void {
    digitalData.custom.addon_prices_offered = this.encodeValue(addonPricesOffered);
  }

  setAddonNamesSelected(addonNamesSelected: string): void {
    digitalData.custom.addon_names_selected = this.encodeValue(addonNamesSelected);
  }

  setAddonPricesSelected(addonPricesSelected: string): void {
    digitalData.custom.addon_prices_selected = this.encodeValue(addonPricesSelected);
  }

  setCustomerStatus(situation: InsuranceSituation | undefined, previousInsurer: string | undefined | null): void {
    if (situation === InsuranceSituation.FIRST_INSURANCE) {
      digitalData.custom.customer_status = CustomerStatusDigitalData.NEW_CUSTOMER;
      return;
    }

    if (previousInsurer === PREVIOUS_INSURER_OPTIONS[0].parameterId) {
      digitalData.custom.customer_status = CustomerStatusDigitalData.EXISTING_CUSTOMER;
      return;
    }

    if (previousInsurer === 'other') {
      digitalData.custom.customer_status = CustomerStatusDigitalData.NEW_CUSTOMER;
      return;
    }

    digitalData.custom.customer_status = '';
  }

  setPrevInsurerCanceled(prevInsurerCanceled: boolean): void {
    if (prevInsurerCanceled == undefined) {
      digitalData.custom.prev_insurer_canceled = '';
      return;
    }
    digitalData.custom.prev_insurer_canceled = prevInsurerCanceled ? YesNoDigitalData.YES : YesNoDigitalData.NO;
  }

  setOfferPriceBlocked(offerPriceBlocked: boolean, reason?: OfferPriceBlockedReason): void {
    const currentValue = digitalData.custom.offer_price_blocked?.split(':')[0];

    if (currentValue === YesNoDigitalData.YES) {
      return;
    }
    if (offerPriceBlocked == undefined) {
      digitalData.custom.offer_price_blocked = '';
      return;
    }
    digitalData.custom.offer_price_blocked = offerPriceBlocked
      ? `${YesNoDigitalData.YES}:${reason}`
      : YesNoDigitalData.NO;
  }

  setNumberOfVehicles(numberOfVehicles?: number): void {
    if (!numberOfVehicles) {
      digitalData.custom.number_of_vehicles = '';
      return;
    }
    digitalData.custom.number_of_vehicles = this.encodeValue(numberOfVehicles.toString());
  }

  setVoucher(voucher: string): void {
    if (!voucher) {
      digitalData.custom.voucher = '';
      return;
    }
    digitalData.custom.voucher = this.encodeValue(voucher);
  }
}
